import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pause, Play } from 'lucide-react';
import Picture from '../site/Picture';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

const SLIDE_MS = 4000;

const PHOTOS = [
  { name: 'fleet-three-taxis', altKey: 'fleetThree', size: [1086, 814] },
  { name: 'two-taxis-hotel', altKey: 'twoHotel', size: [960, 720] },
  { name: 'melia-two-taxis', altKey: 'meliaTwo', size: [960, 720] },
  { name: 'melia-entrance', altKey: 'meliaEntrance', size: [960, 720] },
];

// Featured photo with a crossfade + slow zoom, and thumbnails underneath.
// The active thumbnail's progress bar is the timer: when its CSS animation
// ends the gallery advances, so pausing the animation (hover, touch, focus or
// the pause button) pauses the slideshow too. Reduced motion: no autoplay.
function Gallery() {
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const [active, setActive] = useState(0);
  const [held, setHeld] = useState(false);
  const [stopped, setStopped] = useState(false);

  const autoplay = !reducedMotion && !stopped;
  const paused = held || !autoplay;
  const next = () => setActive((i) => (i + 1) % PHOTOS.length);

  return (
    <div
      className="w-full"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onTouchStart={() => setHeld(true)}
      onTouchEnd={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <div
        className="relative aspect-video overflow-hidden rounded-btn bg-navy shadow-card lg:aspect-[16/10]"
        role="group"
        aria-roledescription={t('site.gallery.roleDescription')}
        aria-label={t('site.gallery.label')}
      >
        {PHOTOS.map((p, i) => (
          <div
            key={p.name}
            aria-hidden={i !== active}
            className={`absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none ${
              i === active ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <Picture
              name={`${p.name}-landscape`}
              alt={t(`site.gallery.alt.${p.altKey}`)}
              width={p.size[0]}
              height={p.size[1]}
              className="h-full w-full"
              imgClassName={reducedMotion ? '' : i === active ? 'animate-ken-burns' : 'scale-[1.08]'}
            />
          </div>
        ))}
        {!reducedMotion && (
          <button
            type="button"
            onClick={() => setStopped((s) => !s)}
            aria-label={stopped ? t('site.gallery.play') : t('site.gallery.pause')}
            className="focus-ring-dark absolute bottom-3 right-3 inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 bg-navy/70 text-white backdrop-blur hover:bg-navy"
          >
            {stopped ? <Play size={18} aria-hidden="true" /> : <Pause size={18} aria-hidden="true" />}
          </button>
        )}
      </div>

      <ul className="m-0 mt-2 grid list-none grid-cols-4 gap-2 p-0">
        {PHOTOS.map((p, i) => {
          const isActive = i === active;
          return (
            <li key={p.name}>
              <button
                type="button"
                onClick={() => setActive(i)}
                aria-label={t('site.gallery.show', { n: i + 1, total: PHOTOS.length })}
                aria-current={isActive ? 'true' : undefined}
                className={`focus-ring-light photo-zoom relative block aspect-[3/2] w-full cursor-pointer overflow-hidden rounded-btn border-0 p-0 ring-offset-2 transition-opacity duration-150 ${
                  isActive ? 'ring-[3px] ring-taxi' : 'opacity-75 hover:opacity-100'
                }`}
              >
                <Picture name={`${p.name}-square`} alt="" width={400} height={400} className="h-full w-full" />
                {isActive && autoplay && (
                  <span
                    key={`${active}-progress`}
                    aria-hidden="true"
                    onAnimationEnd={next}
                    className="animate-progress absolute inset-x-0 bottom-0 h-1 origin-left bg-taxi"
                    style={{
                      animationDuration: `${SLIDE_MS}ms`,
                      animationPlayState: paused ? 'paused' : 'running',
                    }}
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default Gallery;
